'use strict';

import React, { useState, useRef, useCallback } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'ag-grid-enterprise';
import 'ag-grid-enterprise/styles/ag-grid.css';
import 'ag-grid-enterprise/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';

/*
 * Primitives shared between performanceDashboardModern.js and
 * transporterDashboardModern.js, kept in their own module rather than
 * exported from either dashboard file. Having the two dashboards import
 * from each other created a circular import that crashed at runtime
 * ("Cannot access 'COLORS' before initialization") because the importing
 * side ran before the const it needed was initialized.
 */

const COLORS = {
  blue: '#2a78d6',
  orange: '#eb6834',
  aqua: '#1baf7a',
  yellow: '#eda100',
  magenta: '#e87ba4',
  green: '#008300',
  violet: '#4a3aa7',
  red: '#e34948',
  good: '#0ca30c',
  warning: '#ff6b6b',
  serious: '#ec835a',
  critical: '#d03b3b',
  grid: '#e7e9ee',
  muted: '#8b91a0',
  ink: '#14181f',
};

const DELIVERED_STATUS_CODES = new Set([5, 8]);
const DELIVERED_CONSIGNMENT_STATUS = new Set(['delivered', 'trip completed']);

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function groupBy(list, keyFn) {
  const map = new Map();
  (list || []).forEach((item) => {
    const key = keyFn(item);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  });
  return map;
}

function distinctValues(list, field) {
  const set = new Set();
  (list || []).forEach((row) => {
    const value = row[field];
    if (value !== undefined && value !== null && value !== '') set.add(value);
  });
  return Array.from(set).sort();
}

function average(nums) {
  const valid = (nums || []).filter((n) => typeof n === 'number' && !isNaN(n));
  if (!valid.length) return 0;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
}

function sum(nums) {
  return (nums || []).reduce((a, b) => a + (b || 0), 0);
}

function round1(n) {
  return Math.round((n + Number.EPSILON) * 10) / 10;
}

function isDeliveredRow(row) {
  if (!row) return false;
  const cs = (row.consignment_status || '').toLowerCase();
  if (DELIVERED_CONSIGNMENT_STATUS.has(cs)) return true;
  return DELIVERED_STATUS_CODES.has(Number(row.status));
}

function statusColorForPercent(pct) {
  if (pct === null || pct === undefined || isNaN(pct)) return COLORS.muted;
  if (pct >= 85) return COLORS.good;
  return COLORS.warning;
}

function statusClassForPercent(pct) {
  if (pct === null || pct === undefined || isNaN(pct)) return '';
  return pct >= 85 ? 'pdm-status-good' : 'pdm-status-warning';
}

function percentValueFormatter(params) {
  return params.value !== null && params.value !== undefined ? `${params.value}%` : '-';
}

function percentCellClass(params) {
  return statusClassForPercent(params.value);
}

/** Generic row x column pivot: aggregateFn receives the array of raw rows
 *  falling into a (rowKey, colKey) cell. */
function buildPivot(rows, rowKeyFn, colKeys, colKeyFn) {
  const cellGroups = new Map();
  const rowKeys = [];
  rows.forEach((r) => {
    const rk = rowKeyFn(r);
    const ck = colKeyFn(r);
    if (rk === null || rk === undefined || ck === null || ck === undefined) return;
    if (!rowKeys.includes(rk)) rowKeys.push(rk);
    const cellKey = `${rk}||${ck}`;
    if (!cellGroups.has(cellKey)) cellGroups.set(cellKey, []);
    cellGroups.get(cellKey).push(r);
  });
  return {
    rowKeys,
    colKeys,
    cell: (rk, ck) => cellGroups.get(`${rk}||${ck}`) || [],
  };
}

function baseChartOptions() {
  return {
    credits: { enabled: false },
    exporting: { enabled: false },
    chart: { style: { fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif' } },
  };
}

function baseAxisLine(categories) {
  return {
    categories,
    lineColor: '#c3c2b7',
    tickColor: '#c3c2b7',
    labels: { style: { color: COLORS.muted, fontSize: '11px' } },
  };
}

function baseValueAxis(title, max) {
  return {
    title: { text: title, style: { color: COLORS.muted, fontSize: '11px' } },
    gridLineColor: COLORS.grid,
    labels: { style: { color: COLORS.muted, fontSize: '11px' } },
    max,
  };
}

const MULTI_SELECT_STYLES = {
  control: (base) => ({ ...base, minHeight: 34, fontSize: 13 }),
  menu: (base) => ({ ...base, fontSize: 13, zIndex: 20 }),
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];

function slugify(text) {
  return (text || 'grid').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

/** Minimum pixel width for a vertical bar chart with `items.length` categories
 *  so charts with many categories get a horizontal scrollbar inside the card
 *  instead of squeezing every bar into the fixed card width. No cap on
 *  count - the whole point is to let every category get its own space via
 *  scroll rather than restrict how many can be shown. */
function chartScrollWidth(items, perItem = 80, threshold = 8) {
  const count = (items || []).length;
  return count > threshold ? `${count * perItem}px` : null;
}

/** Chart card for a native-Highcharts-drilldown bar chart. Every plain chart
 *  card on these dashboards rebuilds its Highcharts `options` as a brand new
 *  object on every render, which is fine for ordinary charts - but for a
 *  drilldown chart it broke re-drilling entirely: highcharts-react-official
 *  calls chart.update() unconditionally on every re-render of its parent
 *  (not just when the data actually changed), and since the class component
 *  driving these dashboards re-renders on ANY state change anywhere on the
 *  page (filters, hovers, unrelated widgets), that update() kept firing
 *  while the user was mid-drill. Highcharts has no way to know the series
 *  it was just handed is the ALWAYS-top-level definition rather than the
 *  currently-drilled-into one, so it silently stomped the drilled view and
 *  corrupted its own click/active-label tracking for that point going
 *  forward - symptoms: drilling back in after drilling up stops working,
 *  and axis labels inconsistently lose their "active/clickable" styling.
 *  The fix is to never call chart.update() in the first place unless the
 *  underlying data genuinely changed: React.memo with a comparator over the
 *  raw inputs (not the freshly-built options object, which is always a new
 *  reference) skips re-rendering - and therefore skips update() - for every
 *  incidental re-render, leaving Highcharts' own drilldown state untouched. */
const DrilldownChartCard = React.memo(function DrilldownChartCard({
  title, subtitle, rows, drilldownMap, measureKey, yTitle, height = 320, buildOptions,
}) {
  const options = buildOptions(rows, drilldownMap, measureKey, yTitle);
  // No cap on how many categories a drilldown chart shows (e.g. every
  // source city, not just the top 20) - a wide category count instead gets
  // a horizontal scrollbar so every bar keeps its own readable width.
  const minWidth = chartScrollWidth(rows);
  return (
    <div className="pdm-card">
      {title && <p className="pdm-card-title">{title}</p>}
      {subtitle && <p className="pdm-card-subtitle">{subtitle}</p>}
      {options ? (
        <div className={minWidth ? 'pdm-chart-flex pdm-chart-scroll' : 'pdm-chart-flex'} style={{ height }}>
          <div style={{ height: '100%', minWidth: minWidth || undefined }}>
            <HighchartsReact
              highcharts={Highcharts}
              containerProps={{ style: { width: '100%', height: '100%' } }}
              options={{ ...options, chart: { ...options.chart, height } }}
              updateArgs={[true, true, true]}
            />
          </div>
        </div>
      ) : (
        <div className="pdm-empty-state">
          <div className="pdm-empty-state-icon">&#9679;</div>
          <strong>No data available</strong>
          <span>Try widening the date range or clearing a filter.</span>
        </div>
      )}
    </div>
  );
}, (prev, next) => (
  prev.rows === next.rows
  && prev.drilldownMap === next.drilldownMap
  && prev.measureKey === next.measureKey
  && prev.yTitle === next.yTitle
  && prev.height === next.height
  && prev.title === next.title
  && prev.subtitle === next.subtitle
));

/** Standardised AG Grid Enterprise shell: title + record count + refresh /
 *  export / full-screen toolbar, fixed-height internal scrolling grid (the
 *  page itself never grows), floating filters, sortable/resizable/pinnable
 *  columns, a Columns/Filters side bar, quick search and a page-size picker.
 *  Every tabular report on both dashboards renders through this component. */
function GridCard({ title, badge, records, columnDefs, pinnedBottomRowData, onRefresh, loading, height = 560, footerNote }) {
  const [quickFilter, setQuickFilter] = useState('');
  const [fullscreen, setFullscreen] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const gridApiRef = useRef(null);
  const columnApiRef = useRef(null);

  const onGridReady = useCallback((params) => {
    gridApiRef.current = params.api;
    columnApiRef.current = params.columnApi;
  }, []);

  const onQuickFilterChange = (e) => {
    const value = e.target.value;
    setQuickFilter(value);
    if (gridApiRef.current) gridApiRef.current.setQuickFilter(value);
  };

  const onPageSizeChange = (e) => {
    const size = Number(e.target.value);
    setPageSize(size);
    if (gridApiRef.current) gridApiRef.current.paginationSetPageSize(size);
  };

  const slug = slugify(title);
  const onExportCsv = () => { if (gridApiRef.current) gridApiRef.current.exportDataAsCsv({ fileName: `${slug}.csv` }); };
  const onExportExcel = () => {
    if (gridApiRef.current && gridApiRef.current.exportDataAsExcel) gridApiRef.current.exportDataAsExcel({ fileName: `${slug}.xlsx` });
  };
  const onAutoSize = () => { if (columnApiRef.current) columnApiRef.current.autoSizeAllColumns(); };
  const toggleFullscreen = () => {
    setFullscreen((prev) => !prev);
    setTimeout(() => { if (gridApiRef.current) gridApiRef.current.sizeColumnsToFit(); }, 0);
  };

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 90,
    tooltipValueGetter: (p) => (p.value !== null && p.value !== undefined ? String(p.value) : ''),
  };

  return (
    <div className={`pdm-card pdm-grid-card${fullscreen ? ' pdm-grid-fullscreen' : ''}`}>
      <div className="pdm-grid-card-header">
        <div className="pdm-grid-card-titles">
          <p className="pdm-card-title">{title}</p>
          <span className="pdm-grid-record-count">{records ? records.length : 0} records</span>
          {badge && <span className="pdm-pending-badge">{badge}</span>}
        </div>
        <div className="pdm-grid-card-toolbar">
          <input className="pdm-quickfilter" placeholder="Search..." value={quickFilter} onChange={onQuickFilterChange} />
          <select className="pdm-page-size-select" value={pageSize} onChange={onPageSizeChange}>
            {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n} / page</option>)}
          </select>
          <button type="button" className="pdm-icon-btn" title="Refresh" onClick={onRefresh}>&#8635;</button>
          <button type="button" className="pdm-icon-btn" title="Auto-size columns" onClick={onAutoSize}>&#8596;</button>
          <button type="button" className="pdm-icon-btn" title="Export CSV" onClick={onExportCsv}>CSV</button>
          <button type="button" className="pdm-icon-btn" title="Export Excel" onClick={onExportExcel}>XLSX</button>
          <button type="button" className="pdm-icon-btn" title={fullscreen ? 'Exit full screen' : 'Full screen'} onClick={toggleFullscreen}>{fullscreen ? '✖' : '⛶'}</button>
        </div>
      </div>
      <div
        className="ag-theme-alpine pdm-aggrid-shell"
        style={{ height: fullscreen ? 'calc(100vh - 160px)' : height }}
      >
        <AgGridReact
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowData={loading ? undefined : records}
          pinnedBottomRowData={pinnedBottomRowData}
          pagination={true}
          paginationPageSize={pageSize}
          sideBar={{ toolPanels: ['columns', 'filters'] }}
          enableRangeSelection={true}
          enableCellTextSelection={true}
          animateRows={true}
          onGridReady={onGridReady}
          overlayNoRowsTemplate="<span class='pdm-ag-overlay'>No rows to show</span>"
          overlayLoadingTemplate="<span class='pdm-ag-overlay'>Loading...</span>"
        />
      </div>
      {footerNote && <p className="pdm-helper-text">{footerNote}</p>}
    </div>
  );
}

export {
  COLORS,
  MULTI_SELECT_STYLES,
  PAGE_SIZE_OPTIONS,
  safeArray,
  groupBy,
  distinctValues,
  average,
  sum,
  round1,
  isDeliveredRow,
  statusColorForPercent,
  statusClassForPercent,
  percentValueFormatter,
  percentCellClass,
  baseChartOptions,
  baseAxisLine,
  baseValueAxis,
  chartScrollWidth,
  buildPivot,
  GridCard,
  DrilldownChartCard,
};
